import { useContext } from "react";
import {
  NewsletterContext,
  NewsletterConfig,
} from "../context/NewsletterContext";
import { useAuth } from "../../../auth/useAuth";
import { getEmbedding } from "@/api/utils";

export const useNewsletterConfig = () => {
  const { session } = useAuth();
  const user = session?.user.user_metadata;
  const accessToken = session?.access_token;
  const newsletterContext = useContext(NewsletterContext);
  const config = newsletterContext.config;
  const preview = newsletterContext.preview;

  if (!newsletterContext) {
    throw new Error("Missing newsletter context");
  }

  const saveNewsletterConfig = async (
    currentPreview = preview,
    isActiveOverride?: boolean,
    frequencyOverride?: NewsletterConfig["frequency"],
    preferredDayOverride?: NewsletterConfig["preferredDay"],
    toneFeedbackOverride?: string,
    topicsEmbeddingOverride?: number[]
  ) => {
    if (!user || !config || !accessToken) {
      throw new Error(
        `Missing data - user: ${!!user}, config: ${!!config}, token: ${!!accessToken}`
      );
    }

    // Use the override if provided, otherwise fall back to the current config value
    const isActive =
      isActiveOverride !== undefined ? isActiveOverride : config.isActive;
    const frequency =
      frequencyOverride !== undefined ? frequencyOverride : config.frequency;
    const preferredDay =
      preferredDayOverride !== undefined
        ? preferredDayOverride
        : config.preferredDay;
    const toneFeedback =
      toneFeedbackOverride !== undefined
        ? toneFeedbackOverride
        : config.toneFeedback;
    const topicsEmbedding =
      topicsEmbeddingOverride !== undefined
        ? topicsEmbeddingOverride
        : config.topicsEmbedding;

    console.log("toneFeedback", toneFeedback);

    // Generate embedding for topics if they exist and no embedding is cached
    let finalTopicsEmbedding = topicsEmbedding;
    if (config.topics.length > 0 && !topicsEmbedding) {
      try {
        console.log(
          "Generating embedding for topics:",
          config.topics.join(", ")
        );
        finalTopicsEmbedding = await getEmbedding(config.topics.join(", "));
        console.log(
          "Generated embedding:",
          finalTopicsEmbedding
            ? finalTopicsEmbedding.length + " dimensions"
            : "failed"
        );
      } catch (error) {
        console.warn("Failed to generate embedding:", error);
        // Continue without embedding - will be generated on next save
        finalTopicsEmbedding = undefined;
      }
    }

    try {
      console.log("About to send finalTopicsEmbedding:", finalTopicsEmbedding);
      const response = await fetch(
        "https://supabase-worker.marsescobin.workers.dev/api/newsletter-config",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            user,
            config: {
              ...config,
              isActive,
              frequency,
              preferredDay,
              toneFeedback,
              topicsEmbedding: finalTopicsEmbedding,
            },
            preview: currentPreview,
          }),
        }
      );

      console.log("Response status:", response.status); // Debug log

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to save newsletter config: ${response.status} - ${errorText}`
        );
      }

      return response;
    } catch (error) {
      console.error(
        "saveNewsletterConfig error from useNewsletterConfig:",
        error,
        "\nUser:",
        user,
        "\nConfig:",
        config
      );
      throw error;
    }
  };
  return { saveNewsletterConfig };
};
